import { useCallback, useState } from "react";
import { recordNREvent } from "@checkout-core/newrelic";
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";

export enum PrefetchStates {
    INIT = "INIT",
    IN_PROGRESS = "IN_PROGRESS",
    SUCCESS = "SUCCESS",
    FAILED = "FAILED",
}

export const useImagePrefetch = (
    imgList: {
        cloudinaryId: string;
        options: Record<string, string | number>;
    }[],
    nrEvent: string,
) => {
    const [loadingState, setLoadingState] = useState(PrefetchStates.INIT);

    const filterValidImagesUrls = imgList
        .map(image => {
            if (!isEmpty(image.cloudinaryId)) {
                const src = getCloudinaryUrl(image.cloudinaryId, image.options);
                return !isEmpty(src) ? src : "";
            }
            return "";
        })
        .filter(url => !isEmpty(url));

    const startPrefetching = useCallback(() => {
        if (!filterValidImagesUrls.length) return;

        setLoadingState(PrefetchStates.IN_PROGRESS);

        const allImgPromisesArr = filterValidImagesUrls.map(src => {
            return new Promise<void>((resolve, reject) => {
                const preloadImage = new Image();
                preloadImage.onload = () => resolve();
                preloadImage.onerror = () => reject(new Error(`Invalid image preloader on src: ${src}`));
                preloadImage.src = src;
            });
        });

        Promise.all(allImgPromisesArr)
            .then(() => setLoadingState(PrefetchStates.SUCCESS))
            .catch(error => {
                recordNREvent({ eventType: nrEvent, error: error.message });
                setLoadingState(PrefetchStates.FAILED); // This must be called on any failure
            });
    }, [filterValidImagesUrls, nrEvent]);

    return { loadingState, startPrefetching };
};
